import { useState, useMemo, type CSSProperties } from 'react';
import { Button } from '@helios/ui/button';
import { NavIcon } from '../../shell/shared';
import { useAppointmentsContext } from '../../shell/AppointmentsShellLayout';
import { useAppointmentCalendarSettings } from './useAppointmentCalendarSettings';
import {
  useAppointmentsData,
  appointmentToPrefill,
  type EnrichedAppointment,
} from './useAppointmentsData';

// ─── Constants ───

const COLUMNS = [
  'TIME',
  'PATIENT',
  'PROVIDER',
  'SERVICE / CATEGORY',
  'STATUS',
  'NOTES',
  'ACTIONS',
] as const;

// ─── Helpers ───

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Compute end time string from start time + duration */
function computeEndTime(startTime: string, durationMinutes: number): string {
  const [h, m] = startTime.split(':').map(Number);
  const totalMin = h * 60 + (m || 0) + durationMinutes;
  const endH = Math.floor(totalMin / 60) % 24;
  const endM = totalMin % 60;
  return `${pad2(endH)}:${pad2(endM)}`;
}

// ─── List View Page ───

export function ListViewPage() {
  const { openBookDrawer } = useAppointmentsContext();
  const calSettings = useAppointmentCalendarSettings();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dateFilter, setDateFilter] = useState<'today' | 'yesterday' | 'this-week' | 'this-month'>(
    'today',
  );
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  // Compute date range from dateFilter
  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    const todayStr = toDateStr(now);
    if (dateFilter === 'today') {
      return { startDate: todayStr, endDate: todayStr };
    }
    if (dateFilter === 'yesterday') {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      const yStr = toDateStr(y);
      return { startDate: yStr, endDate: yStr };
    }
    if (dateFilter === 'this-week') {
      const day = now.getDay();
      const diff = day === 0 ? 6 : day - 1; // Monday start
      const mon = new Date(now);
      mon.setDate(mon.getDate() - diff);
      const sun = new Date(mon);
      sun.setDate(sun.getDate() + 6);
      return { startDate: toDateStr(mon), endDate: toDateStr(sun) };
    }
    // this-month
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { startDate: toDateStr(firstDay), endDate: toDateStr(lastDay) };
  }, [dateFilter]);

  const { appointments, lookups } = useAppointmentsData(
    startDate,
    endDate,
    calSettings.colorAppointmentsBy,
  );

  const filtered = useMemo(() => {
    return appointments.filter((a) => {
      if (search) {
        const q = search.toLowerCase();
        const match =
          a.patientName.toLowerCase().includes(q) ||
          a.providerName.toLowerCase().includes(q) ||
          a.serviceName.toLowerCase().includes(q);
        if (!match) return false;
      }
      if (calSettings.filters.status && statusFilter && a.statusId !== statusFilter) return false;
      if (calSettings.filters.provider && providerFilter && a.providerId !== providerFilter)
        return false;
      if (calSettings.filters.category && categoryFilter && a.categoryId !== categoryFilter)
        return false;
      return true;
    });
  }, [appointments, search, statusFilter, providerFilter, categoryFilter, calSettings.filters]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Page Header */}
      <div
        style={{
          height: 'var(--clinic-header-height)',
          padding: '0 var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 'var(--font-size-xl2)',
              fontWeight: 'var(--font-weight-semi)',
              lineHeight: 'var(--line-height-xl2)',
              letterSpacing: '-0.44px',
              color: 'var(--text)',
              margin: 0,
            }}
          >
            Appointments
          </h1>
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              margin: '2px 0 0',
            }}
          >
            Browse and manage scheduled appointments
          </p>
        </div>
        <Button variant="gradient" size="md" onClick={() => openBookDrawer()}>
          + Book a Patient
        </Button>
      </div>

      {/* Filters Bar */}
      <div
        style={{
          padding: 'var(--sp-4) var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        {/* Search */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            maxWidth: 320,
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: 12,
              color: 'var(--text-muted)',
              display: 'flex',
              pointerEvents: 'none',
            }}
          >
            <NavIcon name="search" />
          </div>
          <input
            type="text"
            placeholder="Search patient, provider, service..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              height: 'var(--input-height)',
              padding: '0 var(--input-padding-x) 0 40px',
              borderRadius: 'var(--input-radius)',
              border: '1px solid var(--border-input)',
              background: 'var(--input-bg)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--input-text)',
              outline: 'none',
            }}
          />
        </div>

        {/* Date filter */}
        <select
          style={selectStyle}
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value as typeof dateFilter)}
        >
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="this-week">This Week</option>
          <option value="this-month">This Month</option>
        </select>

        {/* Status filter */}
        {calSettings.filters.status && (
          <select
            style={selectStyle}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Status</option>
            {lookups.statusList.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        )}

        {/* Provider filter */}
        {calSettings.filters.provider && (
          <select
            style={selectStyle}
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
          >
            <option value="">Provider</option>
            {lookups.providerList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.display_name || p.full_name}
              </option>
            ))}
          </select>
        )}

        {/* Category filter */}
        {calSettings.filters.category && (
          <select
            style={selectStyle}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">Category</option>
            {lookups.categoryList.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        )}

        {/* Sliders icon button */}
        <button type="button" style={iconBtnStyle}>
          <NavIcon name="sliders" />
        </button>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {filtered.length === 0 ? (
          /* Empty state */
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'var(--sp-12)',
              color: 'var(--text-muted)',
              gap: 'var(--sp-3)',
            }}
          >
            <NavIcon name="calendar" />
            <span
              style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semi)' }}
            >
              No appointments found
            </span>
            <span style={{ fontSize: 'var(--font-size-sm)' }}>
              Try adjusting your filters or search query
            </span>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <th
                    key={col}
                    style={{
                      padding: 'var(--sp-3) var(--sp-4)',
                      textAlign: 'left',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-semi)',
                      color: 'var(--text-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: '1px solid var(--border)',
                      background: 'var(--bg-surface)',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((appt) => (
                <AppointmentRow
                  key={appt.id}
                  appt={appt}
                  openMenu={openMenu}
                  setOpenMenu={setOpenMenu}
                  onEdit={() => openBookDrawer(appointmentToPrefill(appt))}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Appointment Row ───

function AppointmentRow({
  appt,
  openMenu,
  setOpenMenu,
  onEdit,
}: {
  appt: EnrichedAppointment;
  openMenu: string | null;
  setOpenMenu: (id: string | null) => void;
  onEdit: () => void;
}) {
  const endTime = computeEndTime(appt.startTime, appt.durationMinutes);

  return (
    <tr style={{ borderBottom: '1px solid var(--border)' }}>
      {/* Time */}
      <td style={cellStyle}>
        <div style={{ fontWeight: 'var(--font-weight-semi)', color: 'var(--text)' }}>
          {appt.startTime}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>{endTime}</div>
      </td>

      {/* Patient */}
      <td style={cellStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-full)',
              background: appt.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 'var(--font-weight-bold)',
              color: '#475569',
              flexShrink: 0,
            }}
          >
            {getInitials(appt.patientName)}
          </div>
          <div>
            <div
              style={{
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text)',
              }}
            >
              {appt.patientName}
            </div>
          </div>
        </div>
      </td>

      {/* Provider */}
      <td style={{ ...cellStyle, color: 'var(--text)' }}>{appt.providerName}</td>

      {/* Service / Category */}
      <td style={cellStyle}>
        <div style={{ color: 'var(--text)' }}>{appt.serviceName || '—'}</div>
        {appt.categoryName && (
          <span
            style={{
              display: 'inline-block',
              marginTop: 2,
              padding: '1px 8px',
              borderRadius: 'var(--radius-full)',
              background: appt.color,
              color: '#475569',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)',
              lineHeight: '18px',
            }}
          >
            {appt.categoryName}
          </span>
        )}
      </td>

      {/* Status */}
      <td style={cellStyle}>
        {appt.statusLabel && (
          <span
            style={{
              display: 'inline-block',
              padding: '2px 10px',
              borderRadius: 'var(--radius-full)',
              background: '#f1f5f9',
              color: '#475569',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)',
              lineHeight: '20px',
              whiteSpace: 'nowrap',
            }}
          >
            {appt.statusLabel}
          </span>
        )}
      </td>

      {/* Notes */}
      <td
        style={{
          ...cellStyle,
          color: appt.notes ? 'var(--text)' : 'var(--text-muted)',
          maxWidth: 220,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {appt.notes || '—'}
      </td>

      {/* Actions */}
      <td style={{ ...cellStyle, position: 'relative' }}>
        <button
          type="button"
          onClick={() => setOpenMenu(openMenu === appt.id ? null : appt.id)}
          style={{
            width: 28,
            height: 28,
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: openMenu === appt.id ? 'var(--bg-surface-2)' : 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-muted)',
            padding: 0,
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="5" r="1" />
            <circle cx="12" cy="12" r="1" />
            <circle cx="12" cy="19" r="1" />
          </svg>
        </button>

        {/* Dropdown */}
        {openMenu === appt.id && (
          <>
            {/* Click-away overlay */}
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9,
              }}
              onClick={() => setOpenMenu(null)}
            />
            <div
              style={{
                position: 'absolute',
                right: 'var(--sp-4)',
                top: '100%',
                marginTop: -4,
                width: 180,
                background: 'var(--bg-surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--shadow-lg)',
                zIndex: 10,
                overflow: 'hidden',
              }}
            >
              {['Edit Appointment', 'Cancel Appointment'].map((action) => (
                <button
                  key={action}
                  type="button"
                  onClick={() => {
                    setOpenMenu(null);
                    if (action === 'Edit Appointment') onEdit();
                  }}
                  style={{
                    width: '100%',
                    padding: 'var(--sp-2) var(--sp-3)',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    fontSize: 'var(--font-size-sm)',
                    color: action === 'Cancel Appointment' ? '#dc2626' : 'var(--text)',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.background = 'var(--bg-surface-2)';
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.background = 'none';
                  }}
                >
                  {action}
                </button>
              ))}
            </div>
          </>
        )}
      </td>
    </tr>
  );
}

// ─── Shared Styles ───

const cellStyle: CSSProperties = {
  padding: 'var(--sp-3) var(--sp-4)',
  fontSize: 'var(--font-size-sm)',
  verticalAlign: 'middle',
};

const selectStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--sp-8) 0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--input-text)',
  outline: 'none',
  cursor: 'pointer',
  appearance: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2364748b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
};

const iconBtnStyle: CSSProperties = {
  width: 'var(--input-height)',
  height: 'var(--input-height)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: 'var(--text-muted)',
  padding: 0,
};
